import { useEffect } from 'react';
import { reaction } from 'mobx';
import { useStore } from '../store/store';
import {
  eventLogin,
  eventOnboardingStepPending,
  eventOnboardingCompanyProfileCompleted,
  eventOnboardingUserAccountCompleted,
  eventActivation,
  setAnalyticsCompanyType
} from '../ga4/ga4';
import { COMPANY_PROFILE_FIELDS } from '../utils/constants/fields';

// Activation fires once per user, ever — remember it across sessions in this browser.
const ACTIVATION_LS_PREFIX = 'td_activation_fired_';

const COMPANY_TYPE_FIELD = COMPANY_PROFILE_FIELDS.COMPANY_TYPE;

const useAppAnalytics = () => {
  const { authStore, utilsStore } = useStore();
  const { user } = authStore;

  // Company type for every GA4 event of the signed-in user. The profile loads with the account
  // details, the readable names separately: getOptionName returns the raw value while the options
  // are missing, so wait for both. Covers login, reload, profile edits and logout (profile null).
  useEffect(() => {
    const dispose = reaction(
      () => {
        const value = authStore.userProfileCompany?.[COMPANY_TYPE_FIELD.NAME];
        const options = utilsStore.options?.[COMPANY_TYPE_FIELD.VALUES_KEY];
        const hasValue = value !== undefined && value !== null && value !== '';
        return hasValue && options ? utilsStore.getOptionName(value, COMPANY_TYPE_FIELD) : '';
      },
      (companyType) => setAnalyticsCompanyType(companyType),
      { fireImmediately: true }
    );

    return () => dispose();
  }, [authStore, utilsStore]);

  // Login event. Also seeds user_id + session start for every subsequent event.
  // Use the Cognito `sub` (immutable, non-PII UUID) — NOT `cognito:username`,
  // which is the user's email in this pool and would send PII to GA4 on every
  // event (prohibited by Google Analytics policy). `sub` is the pseudonymous,
  // stable-per-user id GA4's User-ID feature expects for cross-device stitching.
  useEffect(() => {
    if (user) {
      eventLogin(user?.idToken?.payload?.sub);
    }
  }, [user]);

  // Onboarding funnel: step completions + the `activation` Key Event.
  useEffect(() => {
    const dispose = reaction(
      () => ({
        hasInfo: !!authStore.userInfo,
        companyPending: authStore.isUserProfileCompanyCardPending,
        userPending: authStore.isUserProfileCardPending,
        userId: authStore.currentUserId,
        companyType: authStore.userCompanyType
      }),
      (curr, prev) => {
        // Account data not loaded yet — nothing reliable to report.
        if (!curr.hasInfo) return;

        // First observation with account data loaded: mark which steps are still pending.
        if (!prev || !prev.hasInfo) {
          if (curr.companyPending) eventOnboardingStepPending('company_profile');
          if (curr.userPending) eventOnboardingStepPending('user_account');
          return;
        }

        // Step completions (pending -> done transitions).
        if (prev.companyPending && !curr.companyPending) {
          eventOnboardingCompanyProfileCompleted();
        }
        if (prev.userPending && !curr.userPending) {
          eventOnboardingUserAccountCompleted();
        }

        // Activation: both onboarding steps complete for the first time. Fire once per user.
        const bothDoneNow = !curr.companyPending && !curr.userPending;
        const bothDoneBefore = !prev.companyPending && !prev.userPending;
        if (bothDoneNow && !bothDoneBefore && curr.userId) {
          const key = ACTIVATION_LS_PREFIX + curr.userId;
          let alreadyFired = false;
          try {
            alreadyFired = !!localStorage.getItem(key);
            if (!alreadyFired) localStorage.setItem(key, '1');
          } catch {
            // localStorage unavailable — fall through and fire anyway.
          }
          if (!alreadyFired) {
            eventActivation(curr.companyType);
          }
        }
      },
      { fireImmediately: true }
    );

    return () => dispose();
  }, [authStore]);
};

export default useAppAnalytics;
